import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Target,
  Layers,
  Printer,
  GraduationCap,
  Globe,
  TrendingUp,
  Users,
  Leaf,
  Building,
  List,
  Sparkles,
  Clock,
  Scale,
  PenTool,
  CalendarDays,
  FileQuestion,
  Files
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { developmentStudiesCurriculum } from '@/data/developmentStudiesCurriculum';
import { developmentStudiesWeeklyLessons } from '@/data/developmentStudiesWeeklyLessons';
import { 
  year11DevelopmentUnitTests,
  year12DevelopmentUnitTests,
  year13DevelopmentUnitTests
} from '@/data/developmentStudiesUnitTests';
import { 
  year11DevelopmentStudentNotes,
  year12DevelopmentStudentNotes,
  year13DevelopmentStudentNotes
} from '@/data/developmentStudiesStudentNotes';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function DevelopmentStudiesCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(cls => 
            cls.subject?.toLowerCase().includes('development') ||
            cls.subject?.toLowerCase().includes('social')
          )
          .map(cls => cls.yearLevel)
        )].sort();
        
        if (years.length > 0) {
          setTeacherYears(years);
          const yearNum = parseInt(years[0].replace(/\D/g, ''));
          if (yearNum >= 11 && yearNum <= 13) {
            setSelectedYear(`Year ${yearNum}`);
          }
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const curriculum = developmentStudiesCurriculum;
  const yearKey = selectedYear.toLowerCase().replace(' ', '');
  const currentYearData = curriculum.years[yearKey];
  
  const getStudentNotes = () => {
    switch(selectedYear) {
      case "Year 11": return year11DevelopmentStudentNotes;
      case "Year 12": return year12DevelopmentStudentNotes;
      case "Year 13": return year13DevelopmentStudentNotes;
      default: return year11DevelopmentStudentNotes;
    }
  };

  const getUnitTests = () => {
    switch(selectedYear) {
      case "Year 11": return year11DevelopmentUnitTests;
      case "Year 12": return year12DevelopmentUnitTests;
      case "Year 13": return year13DevelopmentUnitTests;
      default: return year11DevelopmentUnitTests;
    }
  };

  const strands = curriculum.strands;
  const studentNotes = getStudentNotes();
  const unitTests = getUnitTests();

  const getStrandIcon = (id) => {
    switch(id) {
      case 'DST1': return <Target className="h-5 w-5 text-blue-600" />;
      case 'DST2': return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'DST3': return <Building className="h-5 w-5 text-purple-600" />;
      case 'DST4': return <Leaf className="h-5 w-5 text-emerald-600" />;
      case 'DST5': return <Users className="h-5 w-5 text-orange-600" />;
      default: return <Globe className="h-5 w-5 text-amber-600" />;
    }
  };

  const getStrandColor = (id) => {
    switch(id) {
      case 'DST1': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'DST2': return 'bg-green-100 text-green-800 border-green-300';
      case 'DST3': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'DST4': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'DST5': return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-amber-100 text-amber-800 border-amber-300';
    }
  };

  const handlePrint = () => {
    window.print();
    toast.success('Curriculum sent to printer');
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <Card className="bg-gradient-to-r from-amber-600 to-orange-700 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl">Development Studies</CardTitle>
                <CardDescription className="text-amber-100">
                  Years 11-13 • Vanuatu National Curriculum
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[120px] bg-white/20 border-white/30 text-white">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Year 11">Year 11</SelectItem>
                  <SelectItem value="Year 12">Year 12</SelectItem>
                  <SelectItem value="Year 13">Year 13</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="secondary" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 p-1 flex-wrap">
          <TabsTrigger value="curriculum" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <BookOpen className="h-4 w-4 mr-2" />
            Curriculum
          </TabsTrigger>
          <TabsTrigger value="schemes" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <CalendarDays className="h-4 w-4 mr-2" />
            Scheme
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white bg-rose-100">
            <FileQuestion className="h-4 w-4 mr-2" />
            Term Exam
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            Study Notes
          </TabsTrigger>
          <TabsTrigger value="tests" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Unit Tests
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <PenTool className="h-4 w-4 mr-2" />
            Lesson Plans
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white bg-blue-100">
            <Files className="h-4 w-4 mr-2" />
            Quick Print
          </TabsTrigger>
          <TabsTrigger value="ai-planner" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Lesson Plans
          </TabsTrigger>
        </TabsList>

        {/* Schemes Tab - Weekly Scheme of Work */}
        <TabsContent value="schemes" className="space-y-6">
          <SubjectSchemeOfWork
            subject="Development Studies"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={developmentStudiesWeeklyLessons}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" className="space-y-6">
          <TermExamGenerator
            subject="Development Studies"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={selectedYear === 'Year 11' ? year11DevelopmentUnitTests : selectedYear === 'Year 12' ? year12DevelopmentUnitTests : year13DevelopmentUnitTests}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" className="space-y-6">
          <QuickPrintAll
            subject="Development Studies"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={developmentStudiesWeeklyLessons}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Lesson Plans Tab */}
        <TabsContent value="lesson-plans" className="space-y-6">
          <LessonPlanGenerator
            subject="Development Studies"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={developmentStudiesWeeklyLessons}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum" className="space-y-6">
          {/* Strands Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-amber-600" />
                Five Development Strands
              </CardTitle>
              <CardDescription>Core areas of study in Development Studies</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {strands.map((strand) => (
                  <Card key={strand.id} className={`border-2 ${getStrandColor(strand.id)}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        {getStrandIcon(strand.id)}
                        <span className="font-bold">{strand.id}</span>
                      </div>
                      <h4 className="font-semibold text-sm mb-1">{strand.name}</h4>
                      <p className="text-xs opacity-80">{strand.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Year Overview */}
          {currentYearData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-amber-600" />
                  {selectedYear} Overview
                </CardTitle>
                <CardDescription>
                  {currentYearData.hours} hours total • {currentYearData.hoursPerWeek} hours per week
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <Badge variant="outline" className="text-amber-600 border-amber-600">
                    <Clock className="h-3 w-3 mr-1" />
                    {currentYearData.hours} hours
                  </Badge>
                  <Badge variant="outline" className="text-amber-600 border-amber-600">
                    <List className="h-3 w-3 mr-1" />
                    {currentYearData.units?.length || 0} units
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mb-4">{currentYearData.focus}</p>
                
                {/* Strand Weighting */}
                {currentYearData.strandWeighting && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Strand Weighting:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries(currentYearData.strandWeighting).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm">{strands.find(s => s.id === key)?.name || key}</span>
                          <Badge variant="secondary">{value.weight}% ({value.hours}h)</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Units */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-amber-600" />
                {selectedYear} Units
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-2">
                {currentYearData?.units?.map((unit, index) => (
                  <AccordionItem key={index} value={`unit-${index}`} className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={getStrandColor(unit.strand)}>
                          {unit.strand}
                        </Badge>
                        <span className="font-semibold">{unit.name}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Learning Objectives:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {unit.objectives?.map((obj, i) => (
                            <li key={i}>{obj}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Content:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {unit.content?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">Suggested Activities:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {unit.activities?.map((activity, i) => (
                            <li key={i}>{activity}</li>
                          ))}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Assessment Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-amber-600" />
                Assessment Structure - {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {curriculum.assessment[yearKey] && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="border-2 border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="h-4 w-4 text-amber-600" />
                        Internal Assessment ({curriculum.assessment[yearKey].internal.weighting}%)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600">{curriculum.assessment[yearKey].internal.description}</p>
                      <div className="mt-2">
                        <Progress value={curriculum.assessment[yearKey].internal.weighting} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-orange-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Scale className="h-4 w-4 text-orange-600" />
                        External Assessment ({curriculum.assessment[yearKey].external.weighting}%)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600">{curriculum.assessment[yearKey].external.description}</p>
                      <div className="mt-2">
                        <Progress value={curriculum.assessment[yearKey].external.weighting} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Study Notes Tab */}
        <TabsContent value="notes" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-600" />
                {selectedYear} Study Notes
              </CardTitle>
              <CardDescription>Comprehensive notes for all strands</CardDescription>
            </CardHeader>
            <CardContent>
              {studentNotes?.strands && Object.entries(studentNotes.strands).map(([strandKey, strand]) => (
                <div key={strandKey} className="mb-6">
                  <h3 className="text-lg font-bold text-amber-700 mb-4">{strand.name}</h3>
                  <Accordion type="single" collapsible className="space-y-2">
                    {strand.units?.map((unit, idx) => (
                      <AccordionItem key={idx} value={`notes-${strandKey}-${idx}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <span className="font-semibold">{unit.name}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4 space-y-4">
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Objectives:</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                              {unit.objectives?.map((obj, i) => (
                                <li key={i}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Key Terms:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {unit.keyTerms?.map((term, i) => (
                                <div key={i} className="bg-amber-50 p-2 rounded">
                                  <span className="font-medium text-amber-700">{term.term}:</span>
                                  <span className="text-sm text-gray-600 ml-1">{term.definition}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Study Notes:</h4>
                            <div className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg">
                              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                                {unit.studyNotes}
                              </pre>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Practice Questions:</h4>
                            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                              {unit.practiceQuestions?.map((q, i) => (
                                <li key={i}>{q}</li>
                              ))}
                            </ol>
                          </div>
                          
                          {unit.examTips && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <h4 className="font-semibold text-sm text-yellow-800 mb-1">💡 Exam Tips:</h4>
                              <p className="text-sm text-yellow-700">{unit.examTips}</p>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Tests Tab */}
        <TabsContent value="tests" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-amber-600" />
                {selectedYear} Unit Tests
              </CardTitle>
              <CardDescription>Assessment questions and answers</CardDescription>
            </CardHeader>
            <CardContent>
              {unitTests?.strands && Object.entries(unitTests.strands).map(([strandKey, strand]) => (
                <div key={strandKey} className="mb-6">
                  <h3 className="text-lg font-bold text-amber-700 mb-4">{strand.name}</h3>
                  <Accordion type="single" collapsible className="space-y-2">
                    {strand.tests?.map((test, idx) => (
                      <AccordionItem key={idx} value={`test-${strandKey}-${idx}`} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{test.title}</span>
                            <Badge variant="outline">{test.duration}</Badge>
                            <Badge variant="secondary">{test.totalMarks} marks</Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4">
                          <div className="space-y-4">
                            {test.questions?.map((q, qIdx) => (
                              <Card key={qIdx} className="border">
                                <CardContent className="pt-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="font-semibold text-amber-700">Question {q.id}</span>
                                    <Badge>{q.marks} marks</Badge>
                                  </div>
                                  <div className="mb-3 whitespace-pre-line text-gray-700">{q.question}</div>
                                  <Accordion type="single" collapsible>
                                    <AccordionItem value="answer" className="border-0">
                                      <AccordionTrigger className="text-sm text-amber-600 py-2">
                                        Show Answer
                                      </AccordionTrigger>
                                      <AccordionContent>
                                        <div className="bg-green-50 p-3 rounded whitespace-pre-line text-sm text-gray-700">
                                          {q.answer}
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>
                                  </Accordion>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Lesson Plans Tab */}
        <TabsContent value="ai-planner">
          <AILessonPlanGenerator 
            subject="Development Studies"
            year={selectedYear}
            curriculumData={currentYearData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
